import type { SheetVariant } from '@sunwebgroup/ui-components'
import type { CheckoutServiceParticipant } from '../../../domain/types'
import { useIntl } from '@/intl'
import { getIconForService } from '@/scopes/checkout/components/ServiceIcon'
import {
  DurationSelected,
  OpenSheetButton,
  OptionSelectedTitle,
  ParticipantName,
  PopoverContent,
  PopoverTrigger,
  Price,
  PriceBlock,
  SelectParticipantButton,
  SheetDescription,
  SheetFilterLabel,
  SheetParticipantName,
  SkiAncillaryContent,
} from '@/scopes/checkout/components/services/SkiAncillariesService/SkiAncillaryComponents'
import {
  skiServiceIsBeingEdited,
  useSkiAncillaryServiceStore,
} from '@/scopes/checkout/components/services/SkiAncillariesService/store'
import { saveEditingSkiAncillaryServiceValue, updateEditingSkiAncillaryServiceDuration, updateEditingSkiAncillaryServiceLevel, updateEditingSkiAncillaryServiceSportType, updateEditingSkiAncillaryServiceStartDate, updateEditingSkiAncillaryServiceValue } from '@/scopes/checkout/domain/actions/services/ski'

import { useClickFilterSkiAncillaryService } from '@/scopes/checkout/domain/analytics/useClickFilterSkiAncillaryService'
import { useSendAncillaryAvailable } from '@/scopes/checkout/domain/analytics/useSendAncillaryAvailable'
import { useService } from '@/scopes/checkout/domain/hooks'
import { NoSportType } from '@/scopes/checkout/infrastructure/api/service-mappers/skiAncillaryServiceMapper'
import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import * as Popover from '@radix-ui/react-popover'
import {
  Button,
  FormField,
  FormFieldControl,
  ListItem,
  RadioGroup,
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SpaceBetween,
  tk,
  ToggleGroup,
  Typography,
} from '@sunwebgroup/ui-components'
import { Icon, Tag } from '@sunwebgroup/ui-components/uds'
import { isEmpty, values } from 'ramda'
import * as React from 'react'
import { createSkiServiceEvent } from '../../../domain/analytics'
import { ServiceType } from '../../../domain/types'
import ServiceBox from '../ServiceBox'
import { getDaysRangeFormatted, getPriceDifferenceAndPriceSymbol } from './helpers'
import { skiAncillaryDaysRangeByDurationSelector, skiAncillaryLevelSelector, skiAncillaryServiceDurationsSelector, skiAncillaryServiceEditingValueByIdSelector, skiAncillaryServiceOptionsListSelector, skiAncillarySportTypeSelector } from './selectors'

export function SkiLessonService() {
  const intl = useIntl()
  const { service } = useService(ServiceType.SkiLesson)
  useSendAncillaryAvailable(service, 'selectSkiPass')
  if (!service)
    return null

  const { rooms, participants, options } = service

  if (isEmpty(participants))
    return null
  if (isEmpty(options))
    return null

  return (
    <ServiceBox
      title={intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_title' })}
      description={intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_description' })}
      glyph={getIconForService(ServiceType.SkiLesson)}
    >
      <SpaceBetween direction="y" spacing={tk('spacing_sm')}>
        {rooms?.map(({ participantIds }) => {
          return participantIds.map((participantId) => {
            const participant = participants?.[participantId]
            if (!participant || participant.availableOptions.length === 0) {
              return null
            }

            return (
              <SkiLessonServicePerParticipant
                {...participant}
                key={participantId}
              />
            )
          })
        })}
        {isEmpty(rooms)
        && Object.values(participants ?? {}).map(participant => (
          <SkiLessonServicePerParticipant {...participant} key={participant.id} />
        ))}

        <ParticipantMenuSelection />
      </SpaceBetween>
    </ServiceBox>
  )
}

function ParticipantMenuSelection() {
  const { service } = useService(ServiceType.SkiLesson)
  const [isParticipantMenuOpen, setIsParticipantMenuOpen] = React.useState(false)
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { startEditingService } = useSkiAncillaryServiceStore()

  const handleClick = React.useCallback((participantId: string) => {
    if (!participantId)
      return
    if (!service)
      return
    const { participants } = service

    startEditingService({
      participantId,
      serviceType: ServiceType.SkiLesson,
      isEditing: true,
    })

    if (isParticipantMenuOpen) {
      setIsParticipantMenuOpen(false)
    }

    sendEvent(
      createSkiServiceEvent({
        result: 'Add lesson for',
        type: participants[participantId].name,
        description: 'Ski Lesson',
        element: 'clickable text',
      }),
    )
  }, [isParticipantMenuOpen, startEditingService, service, sendEvent])

  if (!service)
    return null

  const { participants } = service

  const participantsWithoutSkiAncillarySelected = Object.values(participants).filter(({ selectedValue }) => !selectedValue)

  if (!participantsWithoutSkiAncillarySelected.length)
    return null

  function toggle() {
    setIsParticipantMenuOpen(!isParticipantMenuOpen)
  }

  return (
    <ListItem.Root>
      <ListItem.Content>
        {participantsWithoutSkiAncillarySelected.length > 1 && (
          <Popover.Root open={isParticipantMenuOpen} onOpenChange={toggle}>
            <PopoverTrigger>
              <Typography $variant="body-md-bold">{intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_add_button_for' })}</Typography>
            </PopoverTrigger>
            <Popover.Portal>
              <PopoverContent align="center" collisionPadding={{ bottom: 85 }}>
                {participantsWithoutSkiAncillarySelected.map(participant => (
                  <Button key={participant.id} $hierarchy="tertiary" $withoutColor onClick={() => handleClick(participant.id)}>
                    <Typography>{intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_participant_menu_item' }, { name: participant.name })}</Typography>
                  </Button>
                ))}
              </PopoverContent>
            </Popover.Portal>
          </Popover.Root>
        )}
        {participantsWithoutSkiAncillarySelected.length === 1 && (
          <SelectParticipantButton $hierarchy="secondary" onClick={() => handleClick(participantsWithoutSkiAncillarySelected[0].id)} $withoutColor $size="small">
            <Typography>
              {values(participants).length > 1 && intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_add_button_per_participant' }, { name: participantsWithoutSkiAncillarySelected[0].name })}
              {values(participants).length === 1 && intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_add_button' })}
            </Typography>
          </SelectParticipantButton>
        )}
      </ListItem.Content>
    </ListItem.Root>
  )
}

function SkiLessonServicePerParticipant(participant: CheckoutServiceParticipant) {
  const { selectedValue, name, ageCategory: participantAgeCategory } = participant
  const { service } = useService(ServiceType.SkiLesson)
  const { sendEvent } = useAnalytics()
  const { startEditingService } = useSkiAncillaryServiceStore()

  const { isDesktopSm } = useBreakpoints()

  const intl = useIntl()

  const handleClick = React.useCallback(() => {
    if (!participant)
      return
    if (!service)
      return

    startEditingService({
      participantId: participant.id,
      serviceType: ServiceType.SkiLesson,
      isEditing: true,
    })

    sendEvent(
      createSkiServiceEvent({
        result: 'Change',
        type: participant.name,
        description: 'Ski Lesson',
        element: 'element clicked',
      }),
    )
  }, [startEditingService, service, participant])

  if (!service)
    return null

  const { options, participants } = service
  const { startDay, endDay } = selectedValue ? getDaysRangeFormatted(options[selectedValue].startDate, options[selectedValue].endDate, intl.locale) : { startDay: '', endDay: '' }

  const { price = 0, priceBreakdown = [] } = selectedValue ? options[selectedValue] : {}

  const participantPrice = priceBreakdown ? priceBreakdown.find(({ ageCategory }) => ageCategory === participantAgeCategory)?.price : price

  return (
    <>
      { selectedValue
      && (
        <ListItem.Root>
          <ListItem.Content>
            {Object.values(participants).length > 1 && (
              <ParticipantName>
                {intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_for_participant' }, { name })}
              </ParticipantName>
            )}
            <OptionSelectedTitle>
              {selectedValue ? options[selectedValue].label : intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_ancillaries_no_ski_lesson_text' })}
            </OptionSelectedTitle>
            <DurationSelected>
              {(selectedValue && options[selectedValue].duration > '0')
                ? intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_selected_value_duration' }, { duration: options[selectedValue].duration, daysRange: intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_days_range' }, { startDay, endDay }) })
                : ''}
            </DurationSelected>
          </ListItem.Content>
          <ListItem.Trailing>
            <PriceBlock>
              <Price as="span" $variant="label">
                {priceBreakdown?.length
                  ? (
                      intl.formatCurrency(participantPrice ?? 0, participantPrice !== 0)
                    )
                  : (
                      intl.formatCurrency(price, price !== 0)
                    )}
              </Price>
              { !isDesktopSm && (
                <OpenSheetButton $hierarchy="tertiary" $withoutColor $size="small" onClick={handleClick}>
                  <Icon glyph="ChevronRight" size={24} />
                </OpenSheetButton>
              )}
              { isDesktopSm && (
                <Button $hierarchy="secondary" onClick={handleClick} $withoutColor $size="small">
                  {intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_edit_button' })}
                </Button>
              )}
            </PriceBlock>
          </ListItem.Trailing>
        </ListItem.Root>
      )}
      <SkiLessonServiceSheet participant={participant} />
    </>
  )
}

function SkiLessonServiceSheet({ participant }: { participant: CheckoutServiceParticipant }) {
  const { isTablet } = useBreakpoints()
  const skiLessonWrapperSide = isTablet ? 'right' : 'bottom' as SheetVariant
  const intl = useIntl()
  const [isFixed, setIsFixed] = React.useState(false)
  const contentWrapperRef = React.useRef<HTMLDivElement | null>(null)
  const { sendEvent } = useAnalytics()
  const { endEditingService } = useSkiAncillaryServiceStore()

  const { id: participantId, name } = participant
  const { service, setServiceValue } = useService(ServiceType.SkiLesson)

  const handleSaveValue = React.useCallback(() => {
    if (!service)
      return
    const { id } = service.editingSelectedValues[participantId]
    endEditingService(`${ServiceType.SkiLesson}-${participantId}`)
    setServiceValue(saveEditingSkiAncillaryServiceValue({ participantId, value: id })(service))
    sendEvent(
      createSkiServiceEvent({
        result: `Save changes`,
        description: 'Ski lesson',
        element: 'clickable text',
        type: '',
      }),
    )
  }, [sendEvent, participantId, service, endEditingService, setServiceValue])

  const handleCancelClick = React.useCallback(() => {
    if (!participantId)
      return
    if (!service)
      return

    endEditingService(`${ServiceType.SkiLesson}-${participantId}`)

    sendEvent(
      createSkiServiceEvent({
        result: `Cancel`,
        description: 'Ski lesson',
        element: 'clickable text',
        type: '',
      }),
    )
  }, [sendEvent, service, endEditingService, participantId])

  const isParticipantBeingEdited = useSkiAncillaryServiceStore((skiServiceIsBeingEdited(`${ServiceType.SkiLesson}-${participantId}`)))

  React.useEffect(() => {
    const checkHeight = () => {
      const contentHeight = contentWrapperRef.current?.scrollHeight || 0
      const clientHeight = contentWrapperRef.current?.clientHeight || 0
      setIsFixed(contentHeight > clientHeight)
    }

    const resizeObserver = new ResizeObserver(() => {
      checkHeight()
    })

    if (contentWrapperRef.current) {
      resizeObserver.observe(contentWrapperRef.current)
    }

    if (isParticipantBeingEdited) {
      setTimeout(() => {
        checkHeight()
      }, 50)
    }
    window.addEventListener('resize', checkHeight)

    return () => {
      window.removeEventListener('resize', checkHeight)

      if (contentWrapperRef.current) {
        resizeObserver.unobserve(contentWrapperRef.current)
      }
    }
  }, [isParticipantBeingEdited])

  if (!service)
    return null
  const { participants } = service

  return (
    <Sheet modal open={isParticipantBeingEdited}>
      <SheetPortal>
        <SheetOverlay onClick={handleCancelClick} />
        <SheetContent $side={skiLessonWrapperSide}>
          <SheetHeader>
            <SheetTitle>{intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_sheet_title' })}</SheetTitle>
            <SheetClose onClick={handleCancelClick}>{intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_ancillaries_cancel_text_button' })}</SheetClose>
          </SheetHeader>
          <SkiAncillaryContent ref={contentWrapperRef}>
            <SpaceBetween direction="y" spacing={tk('spacing_md')}>
              <SpaceBetween direction="y" spacing={tk('spacing_xs')}>
                {participants && Object.values(participants).length > 1 && <SheetParticipantName $variant="body-lg-bold">{intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_ancillaries_for_participant_label' }, { name })}</SheetParticipantName>}
                <SheetDescription $variant="body-md">
                  {intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_sheet_description' })}
                </SheetDescription>
              </SpaceBetween>
              <SportTypeFilter participantId={participantId} />
              <DurationFilter participantId={participantId} />
              <RangeDateFilter participantId={participantId} />
              <LevelFilter participantId={participantId} />
              <SelectOptionFilter participantId={participantId} />
            </SpaceBetween>
          </SkiAncillaryContent>
          <SheetFooter $isFixed={isFixed}>
            <SpaceBetween direction="x" spacing={tk('spacing_xs')} justifyContent="end">
              <Button $hierarchy="primary" onClick={handleSaveValue} $fullSize>
                {intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_ancillaries_accept_text_button' })}
              </Button>
            </SpaceBetween>
          </SheetFooter>
        </SheetContent>
      </SheetPortal>
    </Sheet>
  )
}

function SportTypeFilter({ participantId }: { participantId: string }) {
  const intl = useIntl()
  const { service, setServiceLocalValue } = useService(ServiceType.SkiLesson)
  const { sendSkiAncillarySportTypeFilterClickEvent } = useClickFilterSkiAncillaryService(service, 'Ski lesson')

  const handleSportTypeChange = React.useCallback((value: string) => {
    if (!value)
      return
    setServiceLocalValue(
      updateEditingSkiAncillaryServiceSportType({
        participantId,
        value,
      }),
    )
    sendSkiAncillarySportTypeFilterClickEvent(value)
  }, [sendSkiAncillarySportTypeFilterClickEvent, participantId, setServiceLocalValue])

  if (!service)
    return null
  const { sportType: sportTypeSelected } = skiAncillaryServiceEditingValueByIdSelector(service, participantId) || {}
  const sportTypes = skiAncillarySportTypeSelector(service)

  return (
    <FormField as="div">
      <SheetFilterLabel><Typography>{intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_sheet_sport_type_text' })}</Typography></SheetFilterLabel>
      <FormFieldControl>
        <ToggleGroup type="single" defaultValue={sportTypeSelected ?? ''} onValueChange={handleSportTypeChange}>
          {sportTypes.map(sportType => (
            <ToggleGroup.Item key={sportType} value={sportType} disabled={sportType === sportTypeSelected}>
              {sportType !== NoSportType && sportType}
              {sportType === NoSportType && intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_sheet_no_sport_type_text' })}
            </ToggleGroup.Item>
          ))}
          { !sportTypes.includes(NoSportType) && (
            <ToggleGroup.Item value={NoSportType} disabled={NoSportType === sportTypeSelected}>{intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_sheet_no_sport_type_text' })}</ToggleGroup.Item>
          )}
        </ToggleGroup>
      </FormFieldControl>
    </FormField>
  )
}

function DurationFilter({ participantId }: { participantId: string }) {
  const intl = useIntl()
  const { service, setServiceLocalValue } = useService(ServiceType.SkiLesson)
  const { sendSkiAncillaryDurationFilterClickEvent } = useClickFilterSkiAncillaryService(service, 'Ski lesson')

  const handleDurationChange = React.useCallback((value: string) => {
    if (!value)
      return
    setServiceLocalValue(
      updateEditingSkiAncillaryServiceDuration({
        participantId,
        value,
      }),
    )

    sendSkiAncillaryDurationFilterClickEvent(value)
  }, [sendSkiAncillaryDurationFilterClickEvent, participantId, setServiceLocalValue])

  if (!service)
    return null

  const durations = skiAncillaryServiceDurationsSelector(service)

  const { duration: durationSelected, sportType: sportTypeSelected } = skiAncillaryServiceEditingValueByIdSelector(service, participantId) || {}
  if (sportTypeSelected === NoSportType)
    return null
  return (
    <FormField as="div">
      <SheetFilterLabel><Typography>{intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_sheet_duration_text' })}</Typography></SheetFilterLabel>
      <FormFieldControl>
        <ToggleGroup type="single" defaultValue={durationSelected ?? ''} onValueChange={handleDurationChange}>
          {durations.map(({ value }) => (
            <ToggleGroup.Item key={value} value={value} disabled={value === durationSelected}>
              {value}
            </ToggleGroup.Item>
          ))}
        </ToggleGroup>
      </FormFieldControl>
    </FormField>
  )
}

function RangeDateFilter({ participantId }: { participantId: string }) {
  const intl = useIntl()
  const { service, setServiceLocalValue } = useService(ServiceType.SkiLesson)
  const { sendSkiAncillaryRangeFilterClickEvent } = useClickFilterSkiAncillaryService(service, 'Ski lesson')

  const handleStartDateChange = React.useCallback((value: string) => {
    if (!value)
      return
    setServiceLocalValue(
      updateEditingSkiAncillaryServiceStartDate({
        participantId,
        value,
      }),
    )

    sendSkiAncillaryRangeFilterClickEvent(participantId, value)
  }, [sendSkiAncillaryRangeFilterClickEvent, participantId, setServiceLocalValue])

  if (!service)
    return null
  const { duration: durationSelected, sportType: sportTypeSelected, startDate: startDateSelected } = skiAncillaryServiceEditingValueByIdSelector(service, participantId) || {}

  const availableDaysRange = skiAncillaryDaysRangeByDurationSelector(service, durationSelected ?? '', sportTypeSelected ?? '')

  if (sportTypeSelected === NoSportType)
    return null

  return (
    <FormField as="div">
      <SheetFilterLabel><Typography>{intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_sheet_start_date_text' })}</Typography></SheetFilterLabel>
      <FormFieldControl>
        <RadioGroup.Root value={startDateSelected} onValueChange={handleStartDateChange}>
          <SpaceBetween direction="y" spacing={tk('spacing_xs')}>
            {availableDaysRange.map((range, index) => {
              const { start, end } = range
              const { startDay, endDay } = getDaysRangeFormatted(start, end, intl.locale)

              return (
                <ListItem.Root key={index}>
                  <ListItem.Control>
                    <RadioGroup.Item value={start} />
                  </ListItem.Control>
                  <ListItem.Content>
                    <ListItem.Label>
                      {intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_days_range' }, { startDay, endDay })}
                    </ListItem.Label>
                  </ListItem.Content>
                </ListItem.Root>
              )
            })}
          </SpaceBetween>
        </RadioGroup.Root>
      </FormFieldControl>
    </FormField>
  )
}

function LevelFilter({ participantId }: { participantId: string }) {
  const intl = useIntl()
  const { service, setServiceLocalValue } = useService(ServiceType.SkiLesson)
  const { sendSkiAncillaryLevelFilterClickEvent } = useClickFilterSkiAncillaryService(service, 'Ski lesson')

  const handleLevelChange = React.useCallback((value: string) => {
    if (!value)
      return
    setServiceLocalValue(
      updateEditingSkiAncillaryServiceLevel({
        participantId,
        value,
      }),
    )
    sendSkiAncillaryLevelFilterClickEvent(value)
  }, [sendSkiAncillaryLevelFilterClickEvent, participantId, setServiceLocalValue])

  if (!service)
    return null

  const { level: levelSelected, sportType: sportTypeSelected } = skiAncillaryServiceEditingValueByIdSelector(service, participantId) || {}
  const levels = skiAncillaryLevelSelector(service, sportTypeSelected ?? '', participantId)
  const defaultLevel = levels[0]

  if (sportTypeSelected === NoSportType)
    return null

  return (
    <FormField as="div">
      <SheetFilterLabel><Typography>{intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_sheet_levels_text' })}</Typography></SheetFilterLabel>
      <FormFieldControl>
        <ToggleGroup type="single" value={levelSelected ?? defaultLevel} onValueChange={handleLevelChange}>
          {levels.map(level => (
            <ToggleGroup.Item key={level} value={level} disabled={level === levelSelected}>
              {intl.formatMessage({ id: `checkout_steps_ancillaries_ski_lesson_sheet_levels_${level?.toLowerCase()}` })}
            </ToggleGroup.Item>
          ))}
        </ToggleGroup>
      </FormFieldControl>
      {levelSelected && sportTypeSelected && (
        <Typography $variant="body-md">
          {intl.formatMessage({ id: `checkout_steps_ancillaries_ski_lesson_sheet_levels_hint_${sportTypeSelected.toLowerCase()}_${levelSelected.toLowerCase()}` })}
        </Typography>
      ) }
    </FormField>
  )
}

function SelectOptionFilter({ participantId }: { participantId: string }) {
  const intl = useIntl()

  const { service, setServiceLocalValue } = useService(ServiceType.SkiLesson)
  const { sendSkiAncillaryOptionFilterClickEvent } = useClickFilterSkiAncillaryService(service, 'Ski lesson', 'Select package')

  const handleValueChange = React.useCallback((value: string) => {
    if (!value)
      return
    setServiceLocalValue(
      updateEditingSkiAncillaryServiceValue({
        participantId,
        value,
      }),
    )
    sendSkiAncillaryOptionFilterClickEvent(value)
  }, [sendSkiAncillaryOptionFilterClickEvent, participantId, setServiceLocalValue])

  if (!service)
    return null

  const { options, participants } = service
  const { duration: durationSelected, id: optionSelectedId, level: levelSelected, sportType: sportTypeSelected, startDate: startDateSelected } = skiAncillaryServiceEditingValueByIdSelector(service, participantId) || {}

  const { selectedValue, availableOptions: availableOptionsParticipant, ageCategory: participantAgeCategory } = participants[participantId]

  const levels = skiAncillaryLevelSelector(service, sportTypeSelected ?? '', participantId)
  const defaultLevel = levels[0]

  const availableOptionsForSelectedDuration = skiAncillaryServiceOptionsListSelector(service, durationSelected ?? '', startDateSelected, sportTypeSelected ?? '', levelSelected ?? defaultLevel)

  if (sportTypeSelected === NoSportType)
    return null
  if (!availableOptionsForSelectedDuration.length)
    return null

  return (
    <FormField as="div">
      <SheetFilterLabel><Typography>{intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_sheet_options_text' })}</Typography></SheetFilterLabel>
      <FormFieldControl>
        <RadioGroup.Root value={optionSelectedId ?? null} onValueChange={handleValueChange}>
          <SpaceBetween direction="y" spacing={tk('spacing_xs')}>
            {availableOptionsForSelectedDuration
              .filter(optionId => availableOptionsParticipant.includes(optionId))
              .map((optionId) => {
                const { id, label, badge, price, priceBreakdown } = options[optionId]
                const participantPrice = priceBreakdown ? priceBreakdown.find(({ ageCategory }) => ageCategory === participantAgeCategory)?.price : price
                const { priceDifference } = getPriceDifferenceAndPriceSymbol(selectedValue, participantPrice ?? price, options, participantAgeCategory)

                return (
                  <ListItem.Root key={optionId}>
                    <ListItem.Control>
                      <RadioGroup.Item value={id} />
                    </ListItem.Control>
                    <ListItem.Content>
                      {badge && <Tag variant="success-tinted" size="M">{badge}</Tag>}
                      <ListItem.Label>{label}</ListItem.Label>
                    </ListItem.Content>
                    <ListItem.Trailing>
                      <Price as="span" $variant="label">
                        {intl.formatCurrency(priceDifference, priceDifference !== 0)}
                      </Price>
                    </ListItem.Trailing>
                  </ListItem.Root>
                )
              },
              )}
          </SpaceBetween>
        </RadioGroup.Root>
      </FormFieldControl>
    </FormField>
  )
}
